import React, { useMemo } from 'react';

interface BlockiesIdenticonProps {
  address: string;
  size?: number;
  className?: string;
  scale?: number;
}

// Simple blockies implementation for TON addresses
const BlockiesIdenticon: React.FC<BlockiesIdenticonProps> = ({
  address,
  size = 40,
  className = '',
  scale = 4
}) => {
  const identicon = useMemo(() => {
    if (!address) return null;
    
    // Create a simple deterministic identicon based on address
    const seed = address.toLowerCase();
    const colors = generateColors(seed);
    const pattern = generatePattern(seed);
    
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 8 8"
        className={className}
        style={{ borderRadius: '50%' }}
      >
        <rect width="8" height="8" fill={colors.background} />
        {pattern.map((row, y) =>
          row.map((cell, x) => {
            if (cell) {
              return (
                <rect
                  key={`${x}-${y}`}
                  x={x}
                  y={y}
                  width="1"
                  height="1"
                  fill={colors.foreground}
                />
              );
            }
            return null;
          })
        )}
      </svg>
    );
  }, [address, size, className]);
  
  return identicon || (
    <div 
      className={`identicon-placeholder ${className}`}
      style={{ 
        width: size, 
        height: size, 
        borderRadius: '50%',
        backgroundColor: '#333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: size * 0.5
      }}
    >
      ?
    </div>
  );
};

// Generate colors based on address
const generateColors = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const hue = Math.abs(hash) % 360;
  const saturation = 70 + (Math.abs(hash >> 8) % 30);
  const lightness = 40 + (Math.abs(hash >> 16) % 20);
  
  return {
    background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    foreground: `hsl(${(hue + 180) % 360}, ${saturation}%, ${100 - lightness}%)`
  };
};

// Generate 8x8 pattern
const generatePattern = (seed: string): boolean[][] => {
  const pattern: boolean[][] = [];
  
  for (let y = 0; y < 8; y++) {
    pattern[y] = [];
    for (let x = 0; x < 8; x++) {
      // Use hash of address + position to determine pattern
      const index = (y * 8 + x) % seed.length;
      const charCode = seed.charCodeAt(index);
      pattern[y][x] = (charCode % 3) !== 0;
    }
  }
  
  return pattern;
};

export default BlockiesIdenticon;
